import React, { useCallback } from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import GetAppIcon from '@material-ui/icons/GetApp';
import { StatusManager } from 'common/components/StatusManager';
import { SchemaStatuses } from 'constants/schemaStatuses';
import { SummarizeWithAIDrawer, useReferenceCheckSummarization } from 'features/ai-generators';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { getToken } from 'services/Authentication';
import strings from 'strings';
import { FPActionButton, FPIconButton, When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPFormStatus from 'UI/components/molecules/FPFormStatus';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { EnvApiUrls } from 'UI/constants/defaults';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { UIStatus } from 'UI/constants/status';
import { SvgMakeAi } from 'UI/res';

import useReferenceCheckForm from './hooks/useReferenceCheckForm';
import { StyledForm } from './ReferenceCheckDrawer.styled';
import { DrawerMode } from './ReferenceCheckTabLayout.constants';
import { validateReferenceCheck } from './utils';

const MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY =
  window.GPAC_ENV?.MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY ??
  process.env.REACT_APP_MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY;

const MIN_REQUIRED_FIELDS = MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY
  ? Number(MIN_REQUIRED_FIELDS_FOR_AI_SUMMARY)
  : 0;

const BASE_URL = `${EnvApiUrls.API_URL_AWS}/`;
const COPIES = strings.inventoryProfiles.sections.tabs.referenceCheck;

const getDrawerStatus = ({ hasFetchingError, isFetching }) => {
  if (isFetching) return UIStatus.Loading;
  return hasFetchingError ? UIStatus.Error : UIStatus.Success;
};

/**
 * @param {Object} props
 * @param {number} props.candidateId
 * @param {string} [props.candidateName]
 * @param {boolean} props.isOpen
 * @param {boolean} props.isSaved
 * @param {boolean} props.isSaving
 * @param {'create'|'edit'|'view'} [props.mode]
 * @param {Function} props.onClose
 * @param {Function} [props.onEditItemClick]
 * @param {Function} props.onSave
 * @param {Function} props.onSaveError
 * @param {Function} props.onSaveSuccess
 * @param {string} [props.recruiterName]
 * @param {number} [props.referenceId]
 * @param {string} [props.status]
 */
const ReferenceCheckDrawer = ({
  candidateId,
  candidateName,
  isOpen,
  isSaved,
  isSaving,
  mode,
  onClose,
  onEditItemClick,
  onSave,
  onSaveError,
  onSaveSuccess,
  recruiterName,
  referenceId,
  status
}) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const canDownload = checkIfFeatureFlagEnabled(FeatureFlags.ReferenceCheckDownload);
  const showDownloadButton = canDownload && mode === DrawerMode.View;

  const [formState, dispatch] = useReferenceCheckForm({
    referenceId,
    candidateId,
    candidateName: candidateName || '',
    recruiterName: recruiterName || '',
    mode,
    onSaveSuccess,
    onSave,
    onSaveError
  });
  const drawerStatus = getDrawerStatus(formState);

  const {
    aiSummaryState,
    closeSummarizedDrawer,
    handleClickSummarizeWithAI,
    isSummaryWithAiEnabled,
    results
  } = useReferenceCheckSummarization();

  const hasEnoughInfoToSummarize =
    formState.reference?.reference?.progress?.key === SchemaStatuses.Completed ||
    formState.reference?.reference?.progress?.current > MIN_REQUIRED_FIELDS;

  const onCloseModal = useCallback(() => {
    onClose();
    dispatch.handleResetForm();
  }, [dispatch, onClose]);

  const httpOptions = {
    headers: {
      Authorization: `Bearer ${getToken()}`,
      ...(EnvApiUrls.SEND_API_KEY && { 'x-api-key': EnvApiUrls.API_KEY_AWS })
    }
  };

  const commonStyledFormProps = {
    showErrorList: false,
    baseUrl: BASE_URL,
    noHtml5Validate: true,
    httpOptions,
    submitRef: formState.saveButtonRef,
    formData: formState.formData,
    placeholders: formState.placeHolders
  };

  const title = candidateName
    ? strings.formatString(COPIES.title, { candidateName })
    : COPIES.sectionName;
  const subheader = formState.referenceName ? (
    <Box display="flex" alignItems="center" gridGap={8}>
      <Typography variant="body2" color="textSecondary">
        {`${formState.referenceName} / ${
          formState?.formData?.section1?.secondRow?.relationship || ''
        }`}
      </Typography>
      <FPFormStatus
        status={mode !== DrawerMode.Create && mode ? status : SchemaStatuses.Pending}
        shouldShowTotal={false}
      />
    </Box>
  ) : null;

  const handlePrimaryClick = () => {
    if (mode === DrawerMode.View && onEditItemClick) {
      onEditItemClick();
      return;
    }

    formState.saveButtonRef.current.click();
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onCloseModal
      }
    },
    {
      mode: 'save',
      props: {
        type: 'button',
        variant: 'contained',
        onClick: handlePrimaryClick,
        isSaving,
        isSuccess: isSaved && mode === DrawerMode.Create,
        disabled:
          isSaving ||
          formState.isFetching ||
          formState.hasFetchingError ||
          (mode === DrawerMode.View && !onEditItemClick) ||
          (isSaved && mode === DrawerMode.Create),
        initialText: mode === DrawerMode.View ? strings.shared.ui.edit : strings.shared.ui.save,
        onProgressText: strings.shared.ui.saving
      }
    }
  ];

  const additionalHeaderButton = (
    <When condition={showDownloadButton}>
      {formState.isDownloading ? (
        <CircularProgress size={24} />
      ) : (
        <FPIconButton
          onClick={dispatch.handleClickDownload}
          icon={GetAppIcon}
          tooltipProps={{ title: strings.shared.ui.download }}
        />
      )}
    </When>
  );

  return (
    <>
      <FPDrawer
        FPDrawerProps={{
          open: isOpen,
          onClose: onCloseModal
        }}
        CardHeaderProps={{ title, subheader }}
        additionalHeaderButton={additionalHeaderButton}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
        onClose={onCloseModal}
      >
        <StatusManager status={drawerStatus}>
          <When condition={mode === DrawerMode.Create}>
            <StyledForm
              {...commonStyledFormProps}
              schema={formState.fetchedSchemas?.structure}
              uiSchema={formState.fetchedSchemas?.ui}
              onChange={dispatch.handleFormChange}
              customValidate={validateReferenceCheck}
              onSubmit={dispatch.handleSubmit}
            />
          </When>
          <When condition={mode === DrawerMode.Edit}>
            <StyledForm
              {...commonStyledFormProps}
              schema={formState.fetchedSchemas?.structure}
              uiSchema={formState.fetchedSchemas?.ui}
              onChange={dispatch.handleFormChange}
              customValidate={validateReferenceCheck}
              noHTML5Validate
              onSubmit={dispatch.handleUpdate}
            />
          </When>
          <When condition={mode === DrawerMode.View}>
            <>
              <When condition={isSummaryWithAiEnabled}>
                <Box display="flex" justifyContent="flex-end" alignItems="center" gridGap={16}>
                  <When condition={aiSummaryState.isLoading}>
                    <CircularProgress size={20} />
                  </When>
                  <FPActionButton
                    color="primary"
                    disabled={aiSummaryState.isLoading || !hasEnoughInfoToSummarize}
                    icon={SvgMakeAi}
                    onClick={() => handleClickSummarizeWithAI(referenceId)}
                    text={
                      aiSummaryState.isLoading
                        ? COPIES.aiSummary.actionButtonLoading
                        : COPIES.aiSummary.actionButton
                    }
                    variant="outlined"
                    withGradient
                  />
                </Box>
              </When>
              <StyledForm
                {...commonStyledFormProps}
                schema={formState.fetchedSchemas?.structure}
                uiSchema={formState.fetchedSchemas?.ui}
                readonly
              />
            </>
          </When>
        </StatusManager>
      </FPDrawer>

      <When condition={isSummaryWithAiEnabled}>
        <SummarizeWithAIDrawer
          onClose={closeSummarizedDrawer}
          open={aiSummaryState.openDrawer}
          results={results}
          subheader={subheader}
          title={title}
        />
      </When>
    </>
  );
};

export default ReferenceCheckDrawer;
